import { useEffect, useRef } from 'react'
import { postPositionSamples, type PositionSample } from '../api/positionSamples'
import { toastOnce } from '../toast/useToastStore'
import type { Quote } from '../api/types'

const FLUSH_INTERVAL_MS = 5_000

/**
 * Piggybacks on the existing 1s quote polling: every 5 seconds, snapshots the
 * latest quote per active symbol and POSTs to /api/position-samples. The server
 * keys on a 5s bucket, so finer-grained sends would just overwrite the same row.
 *
 * Re-fires whenever the active symbol set changes (so newly-opened positions
 * start being persisted promptly without waiting for the next interval tick).
 */
export function usePersistPositionSamples(
  quotesBySymbol: Map<string, Quote>,
  enabled: boolean,
) {
  // Keep a live ref to the quotes map so the interval callback always reads
  // the latest snapshot without re-creating the interval on every quote tick.
  const quotesRef = useRef(quotesBySymbol)
  quotesRef.current = quotesBySymbol

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    function flush() {
      if (cancelled) return
      const samples: PositionSample[] = []
      const now = Date.now()
      for (const [symbol, q] of quotesRef.current) {
        const price = Number(q.last)
        if (!Number.isFinite(price) || price <= 0) continue
        const ts = q.lastTimestamp ? Date.parse(q.lastTimestamp) : now
        samples.push({
          symbol,
          ts: Number.isFinite(ts) ? ts : now,
          price,
        })
      }
      if (!samples.length) return
      postPositionSamples(samples).catch(() => {
        // Best-effort — next tick retries. Notify once so chart gaps
        // aren't a surprise days later.
        toastOnce.error(
          'position-samples-failing',
          'Position price recording is failing — position charts may have gaps.',
        )
      })
    }

    const handle = setInterval(flush, FLUSH_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(handle)
      // Final flush on teardown so the last few seconds aren't lost when the
      // user navigates away from /trades right after closing a position.
      flush()
    }
  }, [enabled])
}
